import React, { useEffect, useMemo, useRef, useState } from "react";
import BsModal from "../Common/BsModal";
import CallOut from "../Common/CallOut";
import Notification from "../Common/Notification";
import { numberFormat } from "../../util/araknerdlib";
import { useSchool } from "../../DataStore/SchoolContext";
import TextInput from "../Common/TextInput";
import ajaxSchool from "../../util/remote/ajaxSchool";

const clean = (v) => String(v ?? "").trim();

const parseCSV = (csvText) => {
  // Simple CSV parser with quotes support
  // Expected headers: student_name, reg_no, fees_balance
  const lines = csvText.replace(/\r/g, "").split("\n").filter((l) => l.trim() !== "");
  if (!lines.length) return { rows: [], errors: ["Empty CSV file."] };

  const splitRow = (line) => {
    const out = [];
    let cur = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        // double quote inside quotes -> escaped quote
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === "," && !inQuotes) {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((x) => clean(x));
  };

  const headersRaw = splitRow(lines[0]).map((h) => h.toLowerCase());
  const idxName = headersRaw.indexOf("student_name");
  const idxReg = headersRaw.indexOf("reg_no");
  const idxBal = headersRaw.indexOf("fees_balance");

  const errors = [];
  if (idxName < 0) errors.push('Missing header: "student_name"');
  if (idxReg < 0) errors.push('Missing header: "reg_no"');
  if (idxBal < 0) errors.push('Missing header: "fees_balance"');

  if (errors.length) return { rows: [], errors };

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitRow(lines[i]);
    if (!cols.some((c) => clean(c) !== "")) continue;

    const student_name = clean(cols[idxName]);
    const reg_no = clean(cols[idxReg]);
    const fees_balance_raw = clean(cols[idxBal]).replace(/,/g, "");
    const fees_balance = Number(fees_balance_raw);

    const rowErrors = [];
    if (!student_name) rowErrors.push("Student Name missing");
    if (!reg_no) rowErrors.push("RegNo missing");
    if (!fees_balance_raw || Number.isNaN(fees_balance)) rowErrors.push("Fees Balance invalid");

    rows.push({
      _row: i + 1,
      student_name,
      reg_no,
      fees_balance: Number.isNaN(fees_balance) ? 0 : fees_balance,
      _errors: rowErrors,
    });
  }

  if (!rows.length) errors.push("No data rows found.");

  return { rows, errors };
};

const downloadTemplate = () => {
  const template =
    "student_name,reg_no,fees_balance\n" +
    "John Doe,S.1234/2026,250000\n" +
    "Jane Smith,S.4321/2026,180000\n";

  const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "fees_defaulters_template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
};

const ImportDefaultersModal = ({ show, onClose, onImport = async () => {} }) => {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const {getBatchListing, batchListing, getFeesVotes, feesVoteListing} = useSchool();

  const [termId, setTermId] = useState("");
  const [voteId, setVoteId] = useState("");
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);

  const fileRef = useRef(null);

  useEffect(()=>{
    getBatchListing();
    getFeesVotes();
  }, [])

  useEffect(() => {
    if(!show){
        setInfo(null);
        setLoading(false);
        setTermId("");
        setVoteId("");
        setFileName("");
        setRows([]);
        setParseErrors([]);
        if (fileRef.current) fileRef.current.value = "";
    }
    }, [show]);

  const validRows = useMemo(() => rows.filter((r) => (r._errors || []).length === 0), [rows]);
  const invalidRows = useMemo(() => rows.filter((r) => (r._errors || []).length > 0), [rows]);

  const handlePickFile = () => {
    if (fileRef.current) fileRef.current.click();
  };

  const handleFileChange = async (e) => {
    setInfo(null);
    setParseErrors([]);
    setRows([]);

    const file = e.target.files?.[0];
    if (!file) return;

    const name = file.name || "selected.csv";
    setFileName(name);

    if (!name.toLowerCase().endsWith(".csv")) {
      setParseErrors(["Please upload a .csv file."]);
      return;
    }

    const text = await file.text();
    const parsed = parseCSV(text);

    setParseErrors(parsed.errors || []);
    setRows(parsed.rows || []);
  };

  const handleSave = async () => {
    setInfo(null);

    if (termId.length === 0) {
      setInfo(<Notification type="danger" message="Please select a billing batch from which this balance is brought forward." closable={false} />);
      return;
    }

    if( voteId.length === 0 ) {
        setInfo(<Notification type="danger" message="Please select a fees vote first where the bill will be applied." closable={false} />);
        return;
    }

    if (!rows.length) {
      setInfo(<Notification type="danger" message="Please upload a CSV file first." closable={false} />);
      return;
    }

    if (parseErrors.length) {
      setInfo(<Notification type="danger" message="Fix CSV errors before importing." closable={false} />);
      return;
    }

    if (invalidRows.length) {
      setInfo(
        <Notification
          type="warning"
          message={`Some rows have issues (${invalidRows.length}). Only valid rows will be imported.`}
          closable={false}
        />
      );
    }

    if (!validRows.length) {
      setInfo(<Notification type="danger" message="No valid rows to import." closable={false} />);
      return;
    }

    setLoading(true);
    const server_response = await ajaxSchool.importFeesDefaulters({
      batch_id: termId,
      vote_id: voteId,
      data: validRows.map((r) => ({
        student_name: r.student_name,
        reg_no: r.reg_no,
        fees_balance: r.fees_balance,
      })),
    });
    setLoading(false);
    if(server_response.status === "OK"){
        setInfo(<Notification type="success" message={server_response.message} closable={false} />);
        onImport();
    }else{
        setInfo(<Notification type="danger" message={server_response.message} closable={false} />);
    }
  };

   const RenderBillingBatch = () => {
      
        const options=[];
        if(Array.isArray(batchListing))
        {
            batchListing.map((item, key)=>
                options.push({label:item.batch_name, value:item.id})
            )
        }
        return(
            <TextInput
                type="select"
                label="Billing Batch"
                placeholder="Select Batch"
                leftIcon="fa fa-calendar-alt"
                options={options}
                onChange={(item)=>setTermId(item?.value)}
                leftComponent={<i className="fa fa-calendar-alt link" onClick={()=>getBatchListing(true)}></i>}
                loading={!batchListing}
                required
                wrap
                value={termId}
        />

        )
    }

     const RenderVotes = () => {
      
        const options=[];
        if(Array.isArray(feesVoteListing))
        {
            feesVoteListing.map((item, key)=>
                options.push({label:item.vote_name, value: item.id})
            )
        }
        return(
            <TextInput
                type="select"
                label="Fees Vote"
                placeholder="Select Vote"
                leftIcon="fa fa-tag"
                options={options}
                onChange={(item)=>setVoteId(item?.value)}
                leftComponent={<i className="fa fa-tag link" onClick={()=>getFeesVotes(true)}></i>}
                loading={!feesVoteListing}
                required
                wrap
                value={voteId}
        />

        )
    }


  return (
    <BsModal
      show={show}
      onClose={onClose}
      title="Import Defaulters"
      size="modal-xl"
      footer={
        <>
          <button type="button" className="btn btn-modern-secondary" onClick={onClose} disabled={loading}>
            Close
          </button>

          <button type="button" className="btn btn-modern-primary" onClick={handleSave} disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Importing...
              </>
            ) : (
              <>
                <i className="fa fa-check-circle mr-2"></i> Confirm Import
              </>
            )}
          </button>
        </>
      }
    >
      <div style={{ padding: 8 }}>
        <CallOut
          title="Import Fees Defaulters"
          description="Download the CSV template, fill it with Student Name, RegNo, and Fees Balance, then upload it here. Preview and confirm import."
        />

        <div className="mt-3">

    {/* ===== Controls ===== */}
    <div className="card mb-3" style={{ border: "1px solid rgba(0,0,0,.08)" }}>
        <div className="card-body">
        <div className="row">
            {/* Term */}
            <div className="col-md-3">
            <div className="tx-12 text-muted mb-1">Step 1</div>
                {RenderBillingBatch()}
            </div>

             {/* Fees Vote */}
            <div className="col-md-3">
            <div className="tx-12 text-muted mb-1">Step 2</div>
                {RenderVotes()}
            </div>

            {/* Template */}
            <div className="col-md-3">
            <div className="tx-12 text-muted mb-1">Step 3</div>
            <label className="tx-13 font-weight-semibold mb-1">Template</label>
            <button
                type="button"
                className="btn btn-outline-primary btn-block"
                onClick={downloadTemplate}
                disabled={loading}
            >
                <i className="fa fa-download mr-2"></i>
                Download CSV Template
            </button>
            <div className="tx-11 text-muted mt-1">
                Required data format
            </div>
            </div>

            {/* Upload */}
            <div className="col-md-3">
            <div className="tx-12 text-muted mb-1">Step 4</div>
            <label className="tx-13 font-weight-semibold mb-1">Upload</label>

            <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={loading}
            />

            <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={handlePickFile}
                disabled={loading}
            >
                <i className="fa fa-upload mr-2"></i>
                Choose CSV File
            </button>

            <div className="tx-12 mt-1">
                {fileName ? (
                <>
                    <span className="text-muted">Selected:</span>{" "}
                    <b>{fileName}</b>
                </>
                ) : (
                <span className="text-muted">No file selected</span>
                )}
            </div>
            </div>
        </div>

        {/* Errors / Info */}
        {parseErrors.length > 0 && (
            <div className="alert alert-danger mt-3 mb-0">
            <div className="tx-12 font-weight-semibold mb-1">CSV Issues</div>
            <ul className="mb-0 pl-3">
                {parseErrors.map((e, i) => (
                <li key={i} className="tx-12">{e}</li>
                ))}
            </ul>
            </div>
        )}

        {info && <div className="mt-3">{info}</div>}
        </div>
    </div>

    {/* ===== Preview (FULL WIDTH) ===== */}
    <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="tx-15 font-weight-semibold">Preview</div>
        <div className="tx-12 text-muted">
        Valid: <b>{validRows.length}</b> | Issues: <b>{invalidRows.length}</b>
        </div>
    </div>

    <div className="card" style={{ border: "1px solid rgba(0,0,0,.08)" }}>
      <div className="card-body p-0">
      <div className="table-responsive">
        <table className="table table-hover mb-0">
          <thead style={{ background: "rgba(0,0,0,.03)" }}>
            <tr className="tx-12 text-muted">
              <th>Row</th>
              <th>Student Name</th>
              <th>Reg No</th>
              <th>Fees Balance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  Upload a CSV file to preview defaulters here.
                </td>
              </tr>
            )}

            {rows.map((r, idx) => {
              const hasErr = r._errors?.length > 0;
              return (
                <tr key={idx} className={hasErr ? "table-warning" : ""}>
                  <td className="tx-12">{r._row}</td>
                  <td className="tx-13 font-weight-semibold">
                    {r.student_name || "—"}
                  </td>
                  <td className="tx-12">{r.reg_no || "—"}</td>
                  <td className="tx-13 text-right">
                    {numberFormat(r.fees_balance)}
                  </td>
                  <td className="tx-12">
                    {hasErr ? (
                      <span className="text-danger">
                        <i className="fa fa-exclamation-circle mr-1"></i>
                        {r._errors.join(", ")}
                      </span>
                    ) : (
                      <span className="text-success">
                        <i className="fa fa-check-circle mr-1"></i>
                        Ready
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

    {rows.length > 0 && (
      <div className="card-footer d-flex justify-content-between align-items-center">
        <div className="tx-12 text-muted">
          Only <b>valid</b> rows will be imported.
        </div>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => {
            setRows([]);
            setParseErrors([]);
            setFileName("");
            if (fileRef.current) fileRef.current.value = "";
          }}
          disabled={loading}
        >
          <i className="fa fa-trash mr-1"></i> Clear
        </button>
      </div>
    )}
  </div>
</div>

      </div>
    </BsModal>
  );
};

export default ImportDefaultersModal;
